import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  inject,
  EventEmitter,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PWAService } from '../../../core/pwa.service';

export interface CapturedPhoto {
  blob: Blob;
  dataUrl: string;
  timestamp: number;
  fileName: string;
}

@Component({
  selector: 'app-camera-capture',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="camera-container" [class.active]="isOpen">
      <!-- Overlay -->
      <div
        *ngIf="isOpen"
        class="fixed inset-0 bg-black/90 z-50 flex flex-col"
        (click)="closeCamera()"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-4 text-white"
          (click)="$event.stopPropagation()"
        >
          <button
            (click)="closeCamera()"
            class="flex items-center space-x-2 text-white/80 hover:text-white transition-colors"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M19 12H5m7-7-7 7 7 7" />
            </svg>
            <span>Voltar</span>
          </button>

          <h3 class="text-lg font-semibold">{{ title }}</h3>

          <button
            *ngIf="supportsFrontCamera"
            (click)="switchCamera()"
            class="text-white/80 hover:text-white transition-colors"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="23,4 23,10 17,10" />
              <polyline points="1,20 1,14 7,14" />
              <path
                d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"
              />
            </svg>
          </button>
        </div>

        <!-- Camera Preview -->
        <div
          class="flex-1 relative flex items-center justify-center"
          (click)="$event.stopPropagation()"
        >
          <video
            #videoElement
            *ngIf="!hasError && !capturedPhoto"
            class="max-w-full max-h-full object-contain rounded-lg"
            autoplay
            playsinline
            muted
          ></video>

          <!-- Preview da foto capturada -->
          <div *ngIf="capturedPhoto" class="relative max-w-full max-h-full">
            <img
              [src]="capturedPhoto.dataUrl"
              class="max-w-full max-h-full object-contain rounded-lg"
              alt="Foto capturada"
            />
            <div class="absolute top-4 right-4 bg-black/50 rounded-lg p-2">
              <button
                (click)="retakePhoto()"
                class="text-white hover:text-gray-300 transition-colors mr-2"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="1,4 1,10 7,10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
              <button
                (click)="confirmPhoto()"
                class="text-white hover:text-gray-300 transition-colors"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Erro -->
          <div *ngIf="hasError" class="text-center text-white p-6">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="mx-auto mb-4 text-red-400"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <h4 class="text-lg font-semibold mb-2">Câmera não disponível</h4>
            <p class="text-white/70 mb-4">{{ errorMessage }}</p>
            <button
              (click)="requestCameraPermission()"
              class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Tentar novamente
            </button>
          </div>

          <!-- Loading -->
          <div *ngIf="isLoading" class="text-center text-white">
            <div
              class="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mx-auto mb-4"
            ></div>
            <p>Iniciando câmera...</p>
          </div>
        </div>

        <!-- Controls -->
        <div
          *ngIf="!hasError && !capturedPhoto"
          class="p-6 flex items-center justify-center space-x-4"
          (click)="$event.stopPropagation()"
        >
          <!-- Botão de captura -->
          <button
            (click)="capturePhoto()"
            [disabled]="isLoading"
            class="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-50 shadow-lg"
          >
            <div
              class="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="text-white"
              >
                <path
                  d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
                />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </div>
          </button>
        </div>

        <!-- Flash Effect -->
        <div
          *ngIf="showFlash"
          class="fixed inset-0 bg-white z-10 pointer-events-none"
          [@flashAnimation]
        ></div>
      </div>
    </div>
  `,
  styles: [
    `
      .camera-container {
        position: relative;
      }

      video {
        transform: scaleX(-1); /* Mirror effect for front camera */
      }

      .camera-container.active video {
        transform: scaleX(-1);
      }

      @keyframes flash {
        0% {
          opacity: 0;
        }
        50% {
          opacity: 0.8;
        }
        100% {
          opacity: 0;
        }
      }

      .flash-effect {
        animation: flash 0.3s ease-out;
      }
    `,
  ],
  animations: [],
})
export class CameraCaptureComponent implements OnInit, OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  @Output() photoTaken = new EventEmitter<CapturedPhoto>();
  @Output() closed = new EventEmitter<void>();

  private pwaService = inject(PWAService);
  private mediaStream: MediaStream | null = null;
  private currentFacingMode: 'user' | 'environment' = 'environment';

  isOpen = false;
  isLoading = false;
  hasError = false;
  errorMessage = '';
  supportsFrontCamera = false;
  capturedPhoto: CapturedPhoto | null = null;
  showFlash = false;
  title = 'Capturar Foto';

  ngOnInit(): void {
    // Check if camera is supported
    this.checkCameraSupport();
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  private checkCameraSupport(): void {
    const deviceInfo = this.pwaService.getDeviceInfo();
    if (!deviceInfo.hasCamera) {
      this.hasError = true;
      this.errorMessage = 'Câmera não suportada neste dispositivo';
    }
  }

  async openCamera(title: string = 'Capturar Foto'): Promise<void> {
    this.title = title;
    this.isOpen = true;
    this.hasError = false;
    this.capturedPhoto = null;

    await this.requestCameraPermission();
  }

  closeCamera(): void {
    this.isOpen = false;
    this.stopCamera();
    this.closed.emit();
  }

  async requestCameraPermission(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: this.currentFacingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      // Check if front camera is available
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );
      this.supportsFrontCamera = videoDevices.length > 1;

      // Wait for video element to be available
      setTimeout(() => {
        if (this.videoElement && this.videoElement.nativeElement) {
          this.videoElement.nativeElement.srcObject = this.mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.hasError = true;

      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          this.errorMessage =
            'Permissão de câmera negada. Permita o acesso à câmera nas configurações do navegador.';
        } else if (error.name === 'NotFoundError') {
          this.errorMessage = 'Nenhuma câmera encontrada neste dispositivo.';
        } else if (error.name === 'NotSupportedError') {
          this.errorMessage = 'Câmera não suportada neste navegador.';
        } else {
          this.errorMessage = 'Erro ao acessar a câmera. Tente novamente.';
        }
      }
    } finally {
      this.isLoading = false;
    }
  }

  async switchCamera(): Promise<void> {
    if (!this.supportsFrontCamera) return;

    this.stopCamera();
    this.currentFacingMode =
      this.currentFacingMode === 'user' ? 'environment' : 'user';
    await this.requestCameraPermission();
  }

  capturePhoto(): void {
    if (!this.videoElement || !this.mediaStream) return;

    const video = this.videoElement.nativeElement;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Show flash effect
    this.showFlashEffect();

    // Convert to blob and data URL
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          const timestamp = Date.now();
          const fileName = `photo_${timestamp}.jpg`;

          this.capturedPhoto = {
            blob,
            dataUrl,
            timestamp,
            fileName,
          };
        }
      },
      'image/jpeg',
      0.9
    );
  }

  private showFlashEffect(): void {
    this.showFlash = true;
    setTimeout(() => {
      this.showFlash = false;
    }, 300);
  }

  retakePhoto(): void {
    this.capturedPhoto = null;
  }

  confirmPhoto(): void {
    if (this.capturedPhoto) {
      this.photoTaken.emit(this.capturedPhoto);
      this.closeCamera();
    }
  }

  private stopCamera(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }
}
